/**
 * La plantilla de WhatsApp que propuso «Escribir con IA» para un grupo del oficial (01/11/2026), en un recuadro: el
 * cuerpo editable con sus variables a la vista, cómo le llega a alguien (la previa del editor de plantillas, con los
 * ejemplos), el pie y los botones de baja de la cuenta, y los avisos.
 *
 * - Se guarda con el borrador del grupo (`propuestaIa`). Todavía no es una plantilla de Meta: para mandarla hay que
 *   crearla y que Meta la apruebe («Crear las plantillas», la parte C del plan).
 * - Los avisos son los del backend: las reglas de Meta y las de Mensajes, que la vista previa vuelve a mirar con cada
 *   cambio (`revisando` mientras tanto).
 */

import type { ReactNode } from "react";
import { Loader2, Trash2 } from "lucide-react";
import { PreviaWhatsApp } from "../../wa-oficial/components/PreviaWhatsApp";
import { NOMBRE_DEL_DATO, textoConEjemplos, variablesDelCuerpo } from "../logic/ia";
import type { PropuestaIa } from "../types";

const btnChico =
  "min-h-[44px] px-3.5 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-800 hover:bg-slate-50 inline-flex items-center justify-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400";
const campo = "min-h-[44px] w-full rounded-xl border border-slate-200 bg-white px-3 text-[15px] text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400";

export function PropuestaWhatsApp({
  grupoId,
  p,
  marca,
  avisos,
  revisando,
  alCambiarCuerpo,
  alCambiarEjemplo,
  alDescartar,
}: {
  grupoId: string;
  p: PropuestaIa;
  marca: string;
  /** la lista de avisos, como la dibuja Mensajes */
  avisos: ReactNode;
  /** la vista previa está mirando los cambios */
  revisando: boolean;
  alCambiarCuerpo: (cuerpo: string) => void;
  alCambiarEjemplo: (numero: number, ejemplo: string) => void;
  alDescartar: () => void;
}) {
  const enElTexto = variablesDelCuerpo(p.cuerpo);
  const declaradas = new Set(p.variables.map((v) => v.numero));
  const sinDato = enElTexto.filter((n) => !declaradas.has(n));

  return (
    <section className="space-y-4 rounded-xl border border-slate-200 bg-white p-4" aria-labelledby={`propuesta-${grupoId}`} data-testid="propuesta-ia">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-[1_1_240px] space-y-1">
          <h3 id={`propuesta-${grupoId}`} className="text-base font-semibold text-slate-900">
            Plantilla propuesta por la IA
          </h3>
          <p className="text-sm text-slate-600">
            Se guarda con el borrador. Todavía no es una plantilla de Meta: para mandarla hay que crearla y que Meta la apruebe.
          </p>
        </div>
        <button type="button" className={btnChico} onClick={alDescartar}>
          <Trash2 size={15} aria-hidden="true" />
          Descartar la propuesta
        </button>
      </div>

      <div className="flex flex-wrap items-start gap-5">
        <div className="min-w-0 flex-[1_1_280px] space-y-3.5">
          <label className="block space-y-2">
            <span className="block text-sm font-semibold text-slate-900">Texto de la plantilla</span>
            <textarea
              value={p.cuerpo}
              rows={6}
              maxLength={1024}
              onChange={(e) => alCambiarCuerpo(e.target.value)}
              className="w-full resize-y rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-[15px] leading-normal text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400"
            />
          </label>

          <fieldset className="space-y-2">
            <legend className="text-sm font-semibold text-slate-900">Variables</legend>
            {p.variables.length === 0 && <p className="text-sm text-slate-600">Esta plantilla no tiene variables.</p>}
            <ul className="space-y-2">
              {p.variables.map((v) => (
                <li key={v.numero} className="space-y-1.5 rounded-xl bg-slate-50 px-3 py-2.5">
                  <p className="text-sm text-slate-800">
                    <span className="mr-2 rounded-md bg-violet-100 px-1.5 py-0.5 font-mono text-[13px] text-violet-900">{`{{${v.numero}}}`}</span>
                    <strong>{v.alias}</strong>
                    {v.campoSugerido && <span className="text-slate-600"> · {NOMBRE_DEL_DATO[v.campoSugerido] ?? v.campoSugerido}</span>}
                    {!enElTexto.includes(v.numero) && <span className="ml-2 text-amber-800">ya no está en el texto</span>}
                  </p>
                  <label className="block space-y-1">
                    <span className="block text-[13px] text-slate-600">{`Ejemplo de {{${v.numero}}} (lo mira Meta para aprobar)`}</span>
                    <input value={v.ejemplo} maxLength={1000} onChange={(e) => alCambiarEjemplo(v.numero, e.target.value)} className={campo} />
                  </label>
                </li>
              ))}
            </ul>
            {sinDato.length > 0 && (
              <p className="text-sm text-amber-800">
                {sinDato.map((n) => `{{${n}}}`).join(", ")} no {sinDato.length === 1 ? "tiene" : "tienen"} un dato: sacala del texto o pedile otra propuesta a la IA.
              </p>
            )}
          </fieldset>

          <div className="space-y-1 text-sm text-slate-700">
            <p>
              <span className="font-semibold text-slate-900">Pie:</span> {p.pie || "sin pie"}
            </p>
            <p>
              <span className="font-semibold text-slate-900">Botones:</span> {p.botones.map((x) => x.titulo).join(" · ") || "sin botones"}
            </p>
          </div>

          {revisando && (
            <p className="flex items-center gap-2 text-sm text-slate-500" role="status">
              <Loader2 size={14} className="animate-spin" aria-hidden="true" />
              Revisando las reglas de Meta…
            </p>
          )}
          {avisos}
        </div>

        <div className="min-w-0 flex-[1_1_240px] space-y-2.5">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Así le llega, con los ejemplos</p>
          <PreviaWhatsApp texto={textoConEjemplos(p)} botones={p.botones} nombreMarca={marca || "Tu marca"} compacto />
        </div>
      </div>
    </section>
  );
}
